import React, { useState, useEffect } from "react";

import { Appearance, Box, Button, Callout, Heading, Icon, Text, Table } from "@blasterjs/core";

import {
  CaseStatus,
  CaseWithImages,
  formatCaseStatus,
  ImageAndQueries,
  ImageAndQuery,
  QueryStatus
} from "../models";
import styled from "styled-components";
import Timestamp from "./Timestamp";
import { useAppDispatch, useAppSelector } from "../hooks";
import { LoggedInUser, RolePermissions } from "../permissions";
import ConfirmationDialog from "./ConfirmationDialog";
import { transitionCaseStatus, toggleSidebarExpanded } from "../slices/caseImageViewer";
import { openEditCaseDialog } from "../slices/caseDialog";
import { CollapseIcon } from "./Expandable";
import Avatar from "./Avatar";
import ImageInfo from "./ImageInfo";
import TooltipFlyout, { TooltipLink } from "./Tooltip";
import CaseStatusDialog from "./CaseStatusDialog";
import PutOnHoldCaseDialog from "./PutOnHoldCaseDialog";
import RemoveHoldCaseDialog from "./RemoveHoldCaseDialog";
import { openCaseStatusDialog } from "../slices/caseStatusDialog";
import { openPutOnHoldCaseDialog } from "../slices/putOnHoldCaseDialog";
import { openRemoveHoldCaseDialog } from "../slices/removeHoldCaseDialog";
import { refreshCase } from "../slices/caseImageViewer";
import RedLabel from "./RedLabel";

interface Props {
  readonly studyName: string | null;
  readonly histoCase: CaseWithImages;
  readonly selectedImage: ImageAndQuery | null;
  readonly loggedInUser: LoggedInUser;
  readonly hasAiAnnotations: boolean;
}

const CompactTable = styled(Table)`
  width: 100%;
  word-break: break-word;
  margin-bottom: 1rem;
  td:first-child {
    font-weight: 700;
    color: #231F20;
    font-size: 16px
    padding-right: 1rem;
    width: 12rem;
  }
  td:nth-child(2) {
    font-weight: 400;
    color: #231F20;
    font-size: 16px
    padding-right: 1rem;
    width: 12rem;
    padding-left: 2rem;
  }
`;

const SidebarPanelCase = ({
  studyName,
  histoCase,
  selectedImage,
  loggedInUser,
  hasAiAnnotations
}: Props) => {
  const dispatch = useAppDispatch();
  const putOnHoldDialog = useAppSelector(state => state.putOnHoldCaseDialog);
  const removeHoldDialog = useAppSelector(state => state.removeHoldCaseDialog);
  const caseStatusDialogState = useAppSelector(state => state.caseStatusDialog);

  useEffect(() => {
    if (!putOnHoldDialog.isOpen && !removeHoldDialog.isOpen && !caseStatusDialogState.isOpen) {
      dispatch(refreshCase());
    }
  }, [putOnHoldDialog.isOpen, removeHoldDialog.isOpen, caseStatusDialogState.isOpen, dispatch]);

  const [isCaseConfirmationDialogOpen, setCaseConfirmationDialogOpen] = useState(false);
  const [isMarkAsScored, setMarkAsScored] = useState(false);
  const onTransition = (markAsScored: boolean) => () => {
    setMarkAsScored(markAsScored);
    setCaseConfirmationDialogOpen(true);
  };
  const onCaseQCConfirm = () => {
    setCaseConfirmationDialogOpen(false);
    dispatch(transitionCaseStatus(histoCase.caseWithStatus));
  };
  const onCaseQCCancel = () => {
    setCaseConfirmationDialogOpen(false);
  };

  const confirmMessage = hasAiAnnotations
    ? selectedImage?.image.acceptAiAnnotations
      ? "Are you sure you want to include the Imported Al Annotation Counts in your scoring?"
      : "Are you sure you want to exclude the Imported Al Annotation Counts in your scoring?"
    : "Are you sure you want to change this case's status?";
  const confirmCaseQCTransitionDialog = (
    <ConfirmationDialog
      title={
        isMarkAsScored
          ? hasAiAnnotations
            ? selectedImage?.image.acceptAiAnnotations
              ? "Mark as Scored with Imported Annotations"
              : "Mark as Scored without Imported Annotations"
            : "Mark as Scored"
          : "Confirm Case Status Change"
      }
      message={confirmMessage}
      isOpen={isCaseConfirmationDialogOpen}
      onConfirm={onCaseQCConfirm}
      onCancel={onCaseQCCancel}
    />
  );

  const caseQcButton =
    loggedInUser.can([RolePermissions.AP_CaseViewer_ViewButtonPassISCQC]) &&
    histoCase.caseWithStatus.status === CaseStatus.PendingQC &&
    !histoCase.caseWithStatus.onHold ? (
      <Button appearance="prominent" iconAfter="check" block={true} onClick={onTransition(false)}>
        Pass QC
      </Button>
    ) : loggedInUser.can([RolePermissions.AP_CaseViewer_MarkCaseasScored]) &&
      histoCase.caseWithStatus.status === CaseStatus.Processed &&
      !histoCase.caseWithStatus.onHold ? (
      <Button appearance="prominent" iconAfter="check" block={true} onClick={onTransition(true)}>
        Mark as Scored
      </Button>
    ) : "errorMessage" in histoCase ? (
      <Callout intent="danger">
        <Text as="p">Unable to transition case status</Text>
      </Callout>
    ) : null;

  const userCanEditCaseStatus = loggedInUser.can([RolePermissions.S_CaseListTab_EditCase]);
  const userCanPutOnHoldCases = loggedInUser.can([RolePermissions.IM_CaseListTab_PutoHold]);

  const caseStatusDialog = userCanEditCaseStatus ? <CaseStatusDialog /> : null;
  const casePutOnHoldDialog = userCanPutOnHoldCases ? <PutOnHoldCaseDialog /> : null;
  const caseRemoveHoldDialog = userCanPutOnHoldCases ? <RemoveHoldCaseDialog /> : null;

  const isAdminCase = "siteId" in histoCase.caseWithStatus;

  const editIcon = loggedInUser.can([RolePermissions.S_CaseListTab_EditCase]) && (
    <Button data-tooltip={true} appearance={Appearance.MINIMAL}>
      <Icon name="menu" />
      <TooltipFlyout placement={"left-start"}>
        <TooltipLink
          onClick={() =>
            dispatch(
              openEditCaseDialog({
                id: histoCase.caseWithStatus.id,
                studyId: histoCase.caseWithStatus.studyId,
                refreshSingleCaseOnSuccess: true
              })
            )
          }
        >
          Edit Case Data
        </TooltipLink>
        <TooltipLink
          onClick={() =>
            isAdminCase ? dispatch(openCaseStatusDialog(histoCase.caseWithStatus)) : null
          }
        >
          Revert Case
        </TooltipLink>
        {userCanPutOnHoldCases && isAdminCase && !histoCase.caseWithStatus.onHold && (
          <TooltipLink onClick={() => dispatch(openPutOnHoldCaseDialog(histoCase.caseWithStatus))}>
            Put on Hold
          </TooltipLink>
        )}
        {userCanPutOnHoldCases && isAdminCase && histoCase.caseWithStatus.onHold && (
          <TooltipLink onClick={() => dispatch(openRemoveHoldCaseDialog(histoCase.caseWithStatus))}>
            Remove Hold
          </TooltipLink>
        )}
      </TooltipFlyout>
    </Button>
  );

  function imageQueriesWereClosed(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Closed);
  }

  function imageQueriesNeedResolution(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Open);
  }

  function inQueryChain(imageAndQuery: ImageAndQuery): boolean {
    if ("imageQueries" in histoCase) {
      const allImageQueries: readonly ImageAndQueries[] = histoCase.imageQueries || [];
      const imageQueries: ImageAndQueries =
        allImageQueries.find(iq => {
          return iq.length > 0 && iq.find(x => x.image.id == imageAndQuery.image.id) != null;
        }) || [];
      return imageQueriesNeedResolution(imageQueries) || imageQueriesWereClosed(imageQueries);
    } else {
      return false;
    }
  }

  return (
    <>
      <Box display="flex" width="100%">
        <Heading textAlign="left" mb={2}>
          <div
            style={{
              display: "flex",
              color: "#231F20",
              height: "30px",
              fontWeight: "700",
              fontSize: "20px"
            }}
          >
            <div style={{ paddingBottom: "0px", width: "60px" }}>Case&nbsp;</div>
            <div style={{ height: "20px" }}>
              {histoCase.caseWithStatus.onHold && <RedLabel>ON HOLD</RedLabel>}
            </div>
          </div>
        </Heading>
        <Box ml={"auto"}>
          {editIcon}
          <CollapseIcon onToggle={() => dispatch(toggleSidebarExpanded())} />
        </Box>
      </Box>
      <CompactTable compact={true} className="image-info-table">
        <tbody>
          <tr>
            <th>Histo Procedure ID</th>
          </tr>
          <tr>
            <td>
              <Box>{histoCase.caseWithStatus.histoProcedureId}</Box>
            </td>
          </tr>
          <tr>
            <th>Endo Procedure ID</th>
          </tr>
          <tr>
            <td>
              <Box>{histoCase.caseWithStatus.procId}</Box>
            </td>
          </tr>
          {"subjectId" in histoCase.caseWithStatus &&
            loggedInUser.can([RolePermissions.AP_CaseData_ViewSubjectID]) && (
              <>
                <tr>
                  <th>Subject ID</th>
                </tr>
                <tr>
                  <td>
                    <Box>{histoCase.caseWithStatus.subjectId}</Box>
                  </td>
                </tr>
              </>
            )}
          {"siteId" in histoCase.caseWithStatus &&
            loggedInUser.can([RolePermissions.AP_CaseData_ViewSiteID]) && (
              <>
                <tr>
                  <th>Site ID</th>
                </tr>
                <tr>
                  <td>
                    <Box>{histoCase.caseWithStatus.siteId}</Box>
                  </td>
                </tr>
              </>
            )}
          {"visitId" in histoCase.caseWithStatus &&
            loggedInUser.can([RolePermissions.AP_CaseData_ViewVisitID]) && (
              <>
                <tr>
                  <th>Visit ID</th>
                </tr>
                <tr>
                  <td>
                    <Box>{histoCase.caseWithStatus.visitId}</Box>
                  </td>
                </tr>
              </>
            )}
          <tr>
            <th>Case Created</th>
          </tr>
          <tr>
            <td>
              <Box>
                <Timestamp date={histoCase.caseWithStatus.createdAt} />
              </Box>
            </td>
          </tr>
          <tr>
            <th>Case Status</th>
          </tr>
          <tr>
            <td>
              <Box>{formatCaseStatus(histoCase.caseWithStatus.status)}</Box>
            </td>
          </tr>
          {"readers" in histoCase ? (
            <>
              <tr>
                <th>CR Assigned</th>
              </tr>
              <tr>
                <td>
                  {histoCase.readers.length ? (
                    <span>
                      {histoCase.readers.slice(0, 5).map(readerStats => (
                        <Box key={readerStats.reader.id} ml="1px" mr="1px" display="inline">
                          <Avatar isSmall={true} user={readerStats.reader} />
                        </Box>
                      ))}
                      {histoCase.readers.length > 5 ? "…" : null}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            </>
          ) : null}
          <tr>
            <th>Study Name</th>
          </tr>
          <tr>
            <td>
              <Box>{studyName}</Box>
            </td>
          </tr>
        </tbody>
      </CompactTable>
      <div style={{ marginBottom: "0.2rem" }}>{caseQcButton}</div>
      {confirmCaseQCTransitionDialog}
      {caseStatusDialog}
      {casePutOnHoldDialog}
      {caseRemoveHoldDialog}

      {selectedImage !== null ? (
        <>
          <hr />
          <ImageInfo
            studyName={null}
            imageAndQuery={selectedImage}
            inQueryChain={
              inQueryChain(
                selectedImage
              ) /*"imageQueries" in histoCase && histoCase.imageQueries.length > 0 || selectedImage.image.lastLabQueryId != null*/
            }
          />
        </>
      ) : null}
    </>
  );
};

export default SidebarPanelCase;
